import React from 'react'
import { bool, func } from 'prop-types'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Form, Button, notification, Checkbox } from 'antd'
import { request, getCurrentUrl, generalErrorHandler } from '../../utils'
import {
  mapStateToProps,
  mapDispatchToProps,
  connect
} from '../../reducers/Dispatchers'
import { HttpMethods } from '../../utils/apiHelper'

const FormItem = Form.Item

const GENERAL_TERMS_OF_USAGE_LINK_FR = 'https://opalsolutions.be/wp-content/uploads/Privacy-policy_Opal-Solutions_052018_v2.pdf'
const GENERAL_TERMS_OF_USAGE_LINK_EN = 'https://opalsolutions.be/wp-content/uploads/EN_Privacy-policy_Opal-Solutions_082022.pdf'
const GENERAL_TERMS_OF_USAGE_LINK_NL = 'https://opalsolutions.be/wp-content/uploads/NL_Privacy-policy_Opal-Solutions_082022.pdf'
const GENERAL_TERMS_OF_USAGE_LINK = {
  fr_be: GENERAL_TERMS_OF_USAGE_LINK_FR,
  fr_fr: GENERAL_TERMS_OF_USAGE_LINK_FR,
  en: GENERAL_TERMS_OF_USAGE_LINK_EN,
  nl: GENERAL_TERMS_OF_USAGE_LINK_NL
}

const DEFAULT_OPTINS = Object.freeze([
  Object.freeze({
    id: 1,
    content: 'I have read and accepted the general terms of usage. In particular, I agree to the processing of my personal data by Interneo.',
    required: true,
    link: {
      href: GENERAL_TERMS_OF_USAGE_LINK,
      text: 'General terms of usage'
    }
  }),
  Object.freeze({
    id: 2,
    required: false,
    content: 'I agree that my personal data can be used for recruitment purposes by the institutions using Interneo.',
    link: null
  })
])

/**
 * Blocking terms-and-conditions (optins) acceptance screen, displayed as long as the user's
 * account (Account) has not accepted them. The acceptance happens once per account, whichever
 * profile is being used.
 */
class NormalOptinsForm extends React.Component {
  static propTypes = {
    /**
     * userIsNoStudent -> is true if the current user is not linked to a student. In this case
     * we don't show the "recruitment" optin.
     */
    userIsNoStudent: bool,
    /** called with the token returned by the backend once the optins are saved */
    onOptinsAccepted: func.isRequired
  }

  static defaultProps = {
    userIsNoStudent: false
  }

  constructor (props) {
    super(props)

    this.state = {
      loading: false,
      optins: this.buildOptins()
    }
  }

  componentDidUpdate (prevProps) {
    if (prevProps.t !== this.props.t) {
      this.setState({ optins: this.buildOptins() })
    }
  }

  /** Builds a local copy of the optins (no mutation of the shared model). */
  buildOptins = () => {
    return DEFAULT_OPTINS
      .filter(o => !(this.props.userIsNoStudent && o.id === 2))
      .map(o => ({
        ...o,
        content: this.props.t(o.content),
        link: o.link === null ? null : {
          // href is indexed by language (fr_be/fr_fr/en/nl): any other locale (pt, de…) falls back
          // to the EN version, otherwise href would be `undefined` and would end up injected as
          // such into the optin text sent to the backend.
          href: o.link.href[this.props.getActiveLanguage] ?? o.link.href.en,
          text: this.props.t(o.link.text)
        }
      }))
  }

  handleSubmit = e => {
    e.preventDefault()
    this.props.form.validateFields((err, values) => {
      if (err) { return }

      this.setState({ loading: true })

      const optins = this.state.optins.map((o, index) => {
        let text = o.content
        if (o.link !== null) {
          text += '<br/><br/><a href="' + o.link.href + '" rel="noopener noreferrer" target="_blank">' + o.link.text + '</a>'
        }

        return {
          text: text,
          accepted: values[`check_${o.id}`] || false,
          from: getCurrentUrl()
        }
      })

      request('/user/save-optins', HttpMethods.POST, { optins }, this.props.getUser)
        .then(json => {
          this.setState({ loading: false })
          if (json.error === undefined) {
            notification.success({
              message: this.props.t('optins_form.save.success'),
              placement: 'bottomRight'
            })
            this.props.onOptinsAccepted(json.token)
          }
        })
        .catch(error => {
          generalErrorHandler(error)
          this.setState({ loading: false })
        })
    })
  };

  checkFormFields = (rule, value, callback) => {
    // Only the first checkbox (terms and conditions) is mandatory.
    if (rule.field === 'check_1' && !value) {
      callback(this.props.t('optins_form.acceptance_required'))
    }
    callback()
  };

  render () {
    const { getFieldDecorator } = this.props.form
    const formItemLayout = {
      labelCol: { xs: { span: 24 } },
      wrapperCol: { xs: { span: 24 } }
    }

    const { optins } = this.state

    return (
      <Form onSubmit={this.handleSubmit}>
        <h3 style={{ marginBottom: '20px' }}>
          {this.props.t('optins_form.title')}
        </h3>
        {optins.map(o => {
          return (
            <FormItem key={o.id}>
              {getFieldDecorator('check_' + o.id, { rules: [{ validator: this.checkFormFields }] })(
                <Checkbox style={{ lineHeight: 'initial' }} disabled={this.state.loading}>
                  {o.content}
                  <br />
                  <br />
                  {o.link === null ? null : <a href={o.link.href} rel='noopener noreferrer' target='_blank'>{o.link.text}</a>}
                </Checkbox>
              )}
            </FormItem>
          )
        })}
        <FormItem {...formItemLayout}>
          <Button
            type='primary'
            htmlType='submit'
            className='full-width'
            loading={this.state.loading}
          >
            <FontAwesomeIcon icon='check' />
            &nbsp;{this.props.t('common.confirm')}
          </Button>
        </FormItem>
      </Form>
    )
  }
}

const OptinsForm = Form.create()(NormalOptinsForm)
export default connect(
  mapStateToProps,
  mapDispatchToProps
)(OptinsForm)
